import { enumMembers, type ApiSchema } from "../apiSchema";
import { jsonBody, requestJson } from "../http/httpClient";
import { readBoolean, readInteger, readIntegerRecord, readObject, readString, type JsonObject } from "../http/jsonDecoding";

/**
 * Removing a Run with everything it wrote, through F158 part 3's erasure (Johannes, 3 Nov 23:45:
 * in the app, for the workshop and the demos). A dry run lists what would go and what keeps the
 * Run; its digest is what a person confirms, and only that list is erased. Who may is the route's
 * own rule: `server/src/dmw/api/routes/threads/erasures.py`.
 */

/** What keeps a Run from being removed, as the erasure names it (`ErasureKeptKind`). */
export type KeptKind = ApiSchema<"ErasureKeptKind">;

/** Every kind, in the order the dialog names them. */
export const KEPT_KINDS = enumMembers<KeptKind>({
  running: true,
  forks: true,
  memories: true,
  named_elsewhere: true,
  comments: true,
  change_sets: true,
  releases: true,
  lineage: true,
  written_since: true,
  moved: true,
  started_since: true,
  recorded_since: true,
  already_erased: true,
});

export interface RunRemovalPreview {
  /** Of the listed inventory: what a person confirms. */
  readonly digest: string;
  /** Nothing keeps the Run: confirmed, the route erases it. */
  readonly removable: boolean;
  /** The Run and the Runs its batches started. */
  readonly runs: number;
  readonly batches: number;
  /** Its results in the data model. */
  readonly results: number;
  /** The scrapped change sets it proposed, which go with it. */
  readonly proposals: number;
  readonly kept: Readonly<Partial<Record<KeptKind, number>>>;
}

function erasuresPath(projectId: string): `/${string}` {
  return `/projects/${encodeURIComponent(projectId)}/threads/erasures`;
}

/** What removing the Run would take with it, and what keeps it; changes nothing. */
export function previewRunRemoval(projectId: string, threadId: string, signal?: AbortSignal): Promise<RunRemovalPreview> {
  const body = { thread_ids: [threadId], dry_run: true } satisfies ApiSchema<"RunErasureBody">;
  return requestJson({ method: "POST", path: erasuresPath(projectId), body: jsonBody(body), decodeResponse: decodeRunRemovalPreview, ...(signal === undefined ? {} : { signal }) });
}

/** Removes the Run as the confirmed preview listed it; the route refuses if anything changed since. */
export async function removeRun(projectId: string, threadId: string, confirmed: string): Promise<void> {
  const body = { thread_ids: [threadId], dry_run: false, confirmed } satisfies ApiSchema<"RunErasureBody">;
  await requestJson({ method: "POST", path: erasuresPath(projectId), body: jsonBody(body), decodeResponse: (response) => readObject(response, "removed Run") });
}

export function decodeRunRemovalPreview(value: unknown): RunRemovalPreview {
  const result = readObject(value, "Run removal");
  const summary: JsonObject = readObject(result["summary"], "Run removal summary");
  const kept = readIntegerRecord(summary, "kept");
  return {
    digest: readString(result, "digest"),
    removable: readBoolean(result, "erasable"),
    runs: readInteger(summary, "runs"),
    batches: readInteger(summary, "batches"),
    results: readInteger(summary, "results"),
    proposals: readInteger(summary, "proposals"),
    kept: Object.fromEntries(KEPT_KINDS.flatMap((kind) => (kept[kind] === undefined ? [] : [[kind, kept[kind]]]))),
  };
}
